import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Settings, 
  Calendar as CalendarIcon, 
  Clock,
  Users,
  Loader2,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RotationSetup({ clubId, clubName, membersCount, onSetupComplete }) {
  const [frequency, setFrequency] = useState('weekly');
  const [startDate, setStartDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const frequencies = [
    { value: 'daily', label: 'Daily', description: 'Every day' },
    { value: 'weekly', label: 'Weekly', description: 'Every 7 days' },
    { value: 'biweekly', label: 'Bi-weekly', description: 'Every 14 days' },
    { value: 'monthly', label: 'Monthly', description: 'Every 30 days' },
    { value: 'yearly', label: 'Yearly', description: 'Every 365 days' },
  ];

  const handleSetupRotation = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/clubs/setup-rotation`,
        {
          club_id: clubId,
          payout_frequency: frequency,
          start_date: startDate.toISOString()
        },
        { withCredentials: true }
      );

      toast.success(response.data.message);
      onSetupComplete?.(response.data.rotation_schedule);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup rotation');
    } finally {
      setLoading(false);
    }
  };

  const getCompletionDate = () => {
    const days = {
      daily: 1,
      weekly: 7,
      biweekly: 14,
      monthly: 30,
      yearly: 365
    };
    const totalDays = days[frequency] * membersCount;
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + totalDays);
    return endDate;
  };

  return (
    <Card className="border-stone-200">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Settings className="w-5 h-5 mr-2 text-indigo-600" />
          Setup Rotation Schedule
        </CardTitle>
        <CardDescription>
          Configure the payout rotation for {clubName}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Members Info */}
        <div className="p-4 bg-indigo-50 rounded-lg">
          <div className="flex items-center text-indigo-800">
            <Users className="w-5 h-5 mr-2" />
            <span className="font-medium">{membersCount} Members</span>
          </div>
          <p className="text-sm text-indigo-600 mt-1">
            Each member will receive one payout in the rotation cycle
          </p>
        </div>

        {/* Frequency Selection */}
        <div className="space-y-2">
          <Label>Payout Frequency</Label>
          <Select value={frequency} onValueChange={setFrequency}>
            <SelectTrigger data-testid="frequency-select">
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              {frequencies.map((freq) => (
                <SelectItem key={freq.value} value={freq.value}>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-stone-400" />
                    <div>
                      <div className="font-medium">{freq.label}</div>
                      <div className="text-xs text-stone-500">{freq.description}</div>
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Start Date */}
        <div className="space-y-2">
          <Label>Start Date</Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
                data-testid="start-date-btn"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, 'PPP') : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => {
                  setStartDate(date || new Date());
                  setCalendarOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Summary */}
        <div className="p-4 bg-stone-50 rounded-lg space-y-3">
          <h4 className="font-medium text-stone-900">Rotation Summary</h4>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-stone-500">First Payout</div>
              <div className="font-medium">{format(startDate, 'MMM d, yyyy')}</div>
            </div>
            <div>
              <div className="text-stone-500">Cycle Completion</div>
              <div className="font-medium">{format(getCompletionDate(), 'MMM d, yyyy')}</div>
            </div>
            <div>
              <div className="text-stone-500">Frequency</div>
              <div className="font-medium capitalize">{frequency}</div>
            </div>
            <div>
              <div className="text-stone-500">Total Payouts</div>
              <div className="font-medium">{membersCount}</div>
            </div>
          </div>
        </div>

        {/* Setup Button */}
        <Button
          onClick={handleSetupRotation}
          disabled={loading}
          className="w-full bg-indigo-600 hover:bg-indigo-700"
          data-testid="setup-rotation-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Setting up...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Setup Rotation Schedule
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
